import { useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type QRCodeStyling from 'qr-code-styling'
import {
  BookOpen,
  ImageIcon,
  Download,
  Gift,
  Globe,
  Instagram,
  Loader2,
  QrCode,
  Save,
  BarChart3,
  ChevronDown,
  Info,
  MoreHorizontal,
  Plus,
  Sparkles,
  Star,
  Trash2,
  type LucideIcon,
} from 'lucide-react'
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  buildBrandedQr,
  QR_LOGO_SCALE_MIN,
  QR_LOGO_SCALE_MAX,
  QR_LOGO_SCALE_DEFAULT,
} from '@/lib/brandedQr'
import { useBrandQrLogo } from '@/hooks/useBrandQrLogo'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import {
  marketingQrDotsTypes,
  marketingQrCornerSquareTypes,
  marketingQrCornerDotTypes,
  type MarketingQrCodeItem,
} from '@/lib/form-schemas'
import type {
  APIResponse,
  MarketingQRCodeRow,
  MarketingQRDeviceMix,
  MarketingQRListResponse,
  MarketingQRTimeseriesResponse,
  UpdateMarketingQRRequest,
} from '@/types'

type Style = MarketingQrCodeItem['style']
type StylePatch = Partial<Style>

const PREVIEW_SIZE = 176
const EXPORT_WIDTH = 1024
const FIXED_QUIET_ZONE_MODULES = 3
const FIXED_ERROR_CORRECTION: Style['error_correction'] = 'Q'

const DEFAULT_STYLE: Style = {
  dark_color: '#000000',
  light_color: '#FFFFFF',
  margin_modules: FIXED_QUIET_ZONE_MODULES,
  dots_type: 'square',
  corner_square_type: 'square',
  corner_dot_type: 'square',
  error_correction: FIXED_ERROR_CORRECTION,
  // Default on: a tenant with a logo configured gets branded codes without
  // hunting for a switch. Falls back to a plain QR when no logo is set.
  logo_enabled: true,
  logo_scale: QR_LOGO_SCALE_DEFAULT,
}

function clampFixedStyle(style: Style): Style {
  return {
    ...style,
    margin_modules: FIXED_QUIET_ZONE_MODULES,
    error_correction: FIXED_ERROR_CORRECTION,
  }
}

const COLOR_PRESETS: { name: string; dark: string; light: string }[] = [
  { name: 'High contrast', dark: '#000000', light: '#FFFFFF' },
  { name: 'Navy / white', dark: '#1e3a5f', light: '#FFFFFF' },
  { name: 'Warm dark', dark: '#292524', light: '#fafaf9' },
  { name: 'White on forest', dark: '#ffffff', light: '#1e5a3a' },
]

const LOOK_PRESETS: { name: string; patch: StylePatch }[] = [
  { name: 'Classic square', patch: { dots_type: 'square', corner_square_type: 'square', corner_dot_type: 'square' } },
  { name: 'Café dots', patch: { dots_type: 'dots', corner_square_type: 'dot', corner_dot_type: 'dot' } },
  { name: 'Rounded tiles', patch: { dots_type: 'rounded', corner_square_type: 'rounded', corner_dot_type: 'rounded' } },
  { name: 'Classy (ink)', patch: { dots_type: 'classy', corner_square_type: 'extra-rounded', corner_dot_type: 'dot' } },
]

// Mirrors the backend's rule (marketingQRStyle.LogoEnabled): an absent value
// means branded, only an explicit false means plain. The API normalises this
// on read, so this is belt-and-braces for legacy cached payloads.
function logoOn(style: Style): boolean {
  return style.logo_enabled !== false
}

// The stored value is a fraction of the error-correction budget; admins get
// words. Thresholds sit inside [QR_LOGO_SCALE_MIN, QR_LOGO_SCALE_MAX].
function logoSizeLabel(scale: number | undefined): string {
  const v = scale ?? QR_LOGO_SCALE_DEFAULT
  if (v <= 0.34) return 'Small — safest'
  if (v <= 0.5) return 'Medium'
  return 'Large — test before printing'
}

const MODULE_SHAPE_LABELS: Record<(typeof marketingQrDotsTypes)[number], string> = {
  square: 'Square (classic)',
  dots: 'Dots',
  rounded: 'Rounded tiles',
  'extra-rounded': 'Extra rounded',
  classy: 'Classy',
  'classy-rounded': 'Classy rounded',
}

const FINDER_OUTER_LABELS: Record<(typeof marketingQrCornerSquareTypes)[number], string> = {
  square: 'Square',
  dot: 'Dot / ring',
  rounded: 'Rounded',
  'extra-rounded': 'Extra rounded',
  dots: 'Dots',
  classy: 'Classy',
  'classy-rounded': 'Classy rounded',
}

const FINDER_INNER_LABELS: Record<(typeof marketingQrCornerDotTypes)[number], string> = {
  square: 'Square',
  dot: 'Dot',
  rounded: 'Rounded',
  'extra-rounded': 'Extra rounded',
  dots: 'Dots',
  classy: 'Classy',
  'classy-rounded': 'Classy rounded',
}

// Delegates to the shared branded-QR builder, which owns the one rule that
// must not vary by call site: a centre logo forces error-correction level H.
// See frontend/src/lib/brandedQr.ts for why.
function buildQrCodeStyling(
  data: string,
  style: Style,
  width: number,
  logoDataUrl: string | null
): QRCodeStyling {
  return buildBrandedQr({
    data,
    size: width,
    style: { ...style, margin_modules: FIXED_QUIET_ZONE_MODULES, error_correction: FIXED_ERROR_CORRECTION },
    logoDataUrl,
    logoEnabled: logoOn(style),
    logoScale: style.logo_scale,
  })
}

// The actual data encoded inside the printed QR is the redirect URL — not
// the merchant's target URL — so admins can re-aim a printed code without
// reprinting it. Falls back to the raw target_url when the backend can't
// resolve a public origin (very early local dev) so the preview still works.
function buildScanURL(redirectBase: string, slug: string, targetURL: string): string {
  const base = (redirectBase || '').replace(/\/+$/, '')
  if (base && slug) return `${base}/q/${slug}`
  return targetURL
}

// A printed QR that points nowhere is the most expensive failure on this
// page, so we forgive a missing scheme (owners type "mysite.com") by
// prefixing https:// on blur, and flag anything that still won't parse.
const HAS_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//
const KNOWN_NON_HTTP = /^(mailto:|tel:|sms:)/i

function normalizeTargetUrl(raw: string): string {
  const t = raw.trim()
  if (!t) return ''
  if (HAS_SCHEME.test(t) || KNOWN_NON_HTTP.test(t)) return t
  return `https://${t}`
}

function isProbablyValidUrl(raw: string): boolean {
  const t = raw.trim()
  if (!t) return true // empty isn't "invalid" — just not set yet
  if (KNOWN_NON_HTTP.test(t)) return true
  try {
    new URL(HAS_SCHEME.test(t) ? t : `https://${t}`)
    return true
  } catch {
    return false
  }
}

const DEVICE_BAR_STYLES: Record<keyof MarketingQRDeviceMix, { label: string; className: string }> = {
  ios: { label: 'iOS', className: 'bg-sky-500' },
  android: { label: 'Android', className: 'bg-emerald-500' },
  desktop: { label: 'Desktop', className: 'bg-violet-500' },
  other: { label: 'Other', className: 'bg-muted-foreground/60' },
}

function DeviceMixBar({ mix }: { mix: MarketingQRDeviceMix }) {
  const total = mix.ios + mix.android + mix.desktop + mix.other
  if (total === 0) return null
  const keys: Array<keyof MarketingQRDeviceMix> = ['ios', 'android', 'desktop', 'other']
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm font-medium uppercase tracking-wide text-muted-foreground">
        <span>Devices · 30d</span>
        <span className="font-mono normal-case tracking-normal">{total.toLocaleString()} scans</span>
      </div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
        {keys.map((k) => {
          const v = mix[k]
          if (v === 0) return null
          const pct = (v / total) * 100
          return (
            <div
              key={k}
              className={DEVICE_BAR_STYLES[k].className}
              style={{ width: `${pct}%` }}
              title={`${DEVICE_BAR_STYLES[k].label}: ${v.toLocaleString()} (${pct.toFixed(0)}%)`}
            />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
        {keys.map((k) => {
          const v = mix[k]
          if (v === 0) return null
          const pct = (v / total) * 100
          return (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${DEVICE_BAR_STYLES[k].className}`} aria-hidden />
              <span className="font-medium text-foreground">{DEVICE_BAR_STYLES[k].label}</span>
              <span>
                {pct.toFixed(0)}%
                <span className="text-muted-foreground/70"> ({v.toLocaleString()})</span>
              </span>
            </span>
          )
        })}
      </div>
    </div>
  )
}


function PresetChip({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-md border border-input/80 bg-background px-3 py-2 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/45 hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98]"
    >
      {children}
    </button>
  )
}

function MarketingQrPreview({
  scanUrl,
  style,
  logoDataUrl,
  size = PREVIEW_SIZE,
}: {
  scanUrl: string
  style: Style
  logoDataUrl: string | null
  size?: number
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [err, setErr] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.innerHTML = ''
    const t = scanUrl.trim()
    if (!t) {
      setErr(false)
      return
    }
    setErr(false)
    try {
      const qr = buildQrCodeStyling(t, style, size, logoDataUrl)
      qr.append(el)
    } catch {
      setErr(true)
    }
  }, [
    scanUrl,
    size,
    logoDataUrl,
    style.dark_color,
    style.light_color,
    style.dots_type,
    style.corner_square_type,
    style.corner_dot_type,
    style.logo_enabled,
    style.logo_scale,
  ])

  // For the small in-card thumbnail (size <= 100) the chrome is reduced —
  // smaller border radius, no inner padding — so the QR doesn't shrink
  // below the legibility floor.
  const compact = size <= 100

  if (!scanUrl.trim()) {
    return (
      <div
        className={`flex items-center justify-center border-2 border-dashed border-muted-foreground/25 bg-muted/30 text-center text-[10px] text-muted-foreground ${compact ? 'rounded-md px-1' : 'rounded-xl px-3 text-xs'}`}
        style={{ width: size, height: size }}
      >
        {compact ? '—' : 'Enter a URL to generate a live preview'}
      </div>
    )
  }
  if (err) {
    return (
      <div
        className={`flex items-center justify-center border border-destructive/35 bg-destructive/5 text-destructive text-center px-2 ${compact ? 'rounded-md text-[10px]' : 'rounded-xl text-xs'}`}
        style={{ width: size, height: size }}
      >
        QR error
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`flex items-center justify-center overflow-hidden bg-card dark:bg-card ${compact ? 'rounded-md' : 'rounded-xl border border-border/80 p-1.5 shadow-inner ring-1 ring-black/[0.06] dark:ring-white/10'}`}
      style={{ width: size, minHeight: size }}
    />
  )
}

function formatRelative(ts: string | null | undefined): string {
  if (!ts) return 'Never'
  const t = new Date(ts).getTime()
  if (Number.isNaN(t)) return 'Never'
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`
  if (diffSec < 86400 * 30) return `${Math.round(diffSec / 86400)}d ago`
  return new Date(ts).toLocaleDateString()
}

// Pads bucketed timeseries to a continuous run of days (recharts looks
// jagged when days with zero scans are silently dropped).
function padBuckets(
  buckets: { day: string; scans: number; uniques: number }[],
  days: number,
): { day: string; label: string; scans: number; uniques: number }[] {
  const byDay = new Map(buckets.map((b) => [b.day, b]))
  const out: { day: string; label: string; scans: number; uniques: number }[] = []
  const today = new Date()
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const existing = byDay.get(iso)
    out.push({
      day: iso,
      label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      scans: existing?.scans ?? 0,
      uniques: existing?.uniques ?? 0,
    })
  }
  return out
}

interface SlotState {
  label: string
  target_url: string
  style: Style
  is_active: boolean
}

function rowToState(row: MarketingQRCodeRow): SlotState {
  // The server validates these enum values (validateMarketingQRStyle),
  // so widening the API's plain-string types into the strict Style union
  // is safe — anything else would have been rejected on the way in.
  const merged = { ...DEFAULT_STYLE, ...row.style } as Style
  return {
    label: row.label || '',
    target_url: row.target_url || '',
    style: clampFixedStyle(merged),
    is_active: row.is_active,
  }
}

function statePatchToRequest(prev: SlotState, next: SlotState): UpdateMarketingQRRequest {
  const out: UpdateMarketingQRRequest = {}
  if (prev.label !== next.label) out.label = next.label
  if (prev.target_url !== next.target_url) out.target_url = next.target_url.trim()
  if (prev.is_active !== next.is_active) out.is_active = next.is_active
  const sChanged =
    prev.style.dark_color !== next.style.dark_color ||
    prev.style.light_color !== next.style.light_color ||
    prev.style.dots_type !== next.style.dots_type ||
    prev.style.corner_square_type !== next.style.corner_square_type ||
    prev.style.corner_dot_type !== next.style.corner_dot_type ||
    prev.style.logo_enabled !== next.style.logo_enabled ||
    prev.style.logo_scale !== next.style.logo_scale
  if (sChanged) out.style = next.style
  return out
}

// Inline collapsible. No accordion primitive in ui/, so this is the
// smallest thing that fits — single useState, ChevronDown rotates,
// content hides via conditional render (no max-height animation).
function Disclosure({
  title,
  icon: Icon,
  hint,
  defaultOpen = false,
  children,
}: {
  title: string
  icon?: LucideIcon
  hint?: string
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-t border-border/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/40"
        aria-expanded={open}
      >
        {Icon ? <Icon className="h-4 w-4 text-muted-foreground" aria-hidden /> : null}
        <span className="flex-1">{title}</span>
        {hint && !open ? (
          <span className="hidden text-xs font-normal text-muted-foreground sm:inline">{hint}</span>
        ) : null}
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open ? <div className="border-t border-border/40 bg-muted/20 px-4 py-4">{children}</div> : null}
    </div>
  )
}

// Range options for the per-card "Scan history" disclosure. `days`
// matches the backend timeseries `?days=` param (caps at 730).
const ANALYTICS_RANGES: { days: number; label: string }[] = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
]

function MarketingQRCard({
  row,
  redirectBase,
  state,
  onPatch,
  onSave,
  saving,
  dirty,
  isNew = false,
  onDelete,
}: {
  row: MarketingQRCodeRow
  redirectBase: string
  state: SlotState
  onPatch: (patch: Partial<SlotState> | { style: StylePatch }) => void
  onSave: () => void
  saving: boolean
  dirty: boolean
  isNew?: boolean
  onDelete: () => void
}) {
  const { toast } = useToast()
  const scanUrl = buildScanURL(redirectBase, row.slug, state.target_url)
  const [analyticsDays, setAnalyticsDays] = useState(30)
  const { logoDataUrl, logoUrl } = useBrandQrLogo()

  // Per-slot timeseries — independent React Query so a single slot can
  // refetch (e.g. after a scan demo or range change) without thrashing
  // the others. The `analyticsDays` segment of the query key swaps the
  // dataset when the admin picks a different range inside the disclosure.
  const timeseriesQuery = useQuery<APIResponse<MarketingQRTimeseriesResponse>>({
    queryKey: ['marketing-qr-timeseries', row.id, analyticsDays],
    queryFn: () => apiClient.getMarketingQRTimeseries(row.id, analyticsDays),
    staleTime: 60_000,
  })
  const buckets = useMemo(
    () => padBuckets(timeseriesQuery.data?.data?.buckets ?? [], analyticsDays),
    [timeseriesQuery.data, analyticsDays],
  )
  const totals = timeseriesQuery.data?.data?.totals

  const downloadPng = async () => {
    if (!scanUrl) {
      toast({ title: 'No URL', description: 'Enter a destination URL before downloading.', variant: 'destructive' })
      return
    }
    try {
      const qr = buildQrCodeStyling(scanUrl, state.style, EXPORT_WIDTH, logoDataUrl)
      const blob = await qr.getRawData('png')
      if (!blob || !(blob instanceof Blob)) throw new Error('no blob')
      const safe = (state.label || row.slug).replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 40) || `qr_${row.slug}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `marketing-qr_${safe}.png`
      a.click()
      URL.revokeObjectURL(url)
      toast({
        title: 'Download started',
        description: 'PNG encodes your trackable redirect URL — test on a few phones before bulk printing.',
      })
    } catch {
      toast({ title: 'Export failed', description: 'Could not generate PNG.', variant: 'destructive' })
    }
  }

  const selectedRange = ANALYTICS_RANGES.find((r) => r.days === analyticsDays) ?? ANALYTICS_RANGES[1]
  const allBucketsZero = buckets.every((b) => b.scans === 0)

  return (
    <Card
      className={`flex flex-col overflow-hidden border-border/80 shadow-sm ring-1 ring-black/[0.04] transition-shadow duration-500 dark:ring-white/[0.06] ${
        isNew ? 'ring-2 ring-primary/60 ring-offset-2' : ''
      }`}
    >
      {/* Header — big editable campaign name is the card's identity, so
          scrolling the page is "Menu → Table tent → Google Reviews"
          instead of four identical "QR N" inputs. Below it: status badge,
          slug badge (with the "what the QR contains" tooltip), and the
          kebab for less-frequent actions. Click anywhere on the title
          to rename inline. */}
      <div className="border-b border-border/60 bg-card px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <Input
            id={`mq-label-${row.id}`}
            value={state.label}
            onChange={(e) => onPatch({ label: e.target.value })}
            placeholder="Untitled QR code"
            maxLength={100}
            aria-label="Campaign name"
            title="Click to rename this QR"
            className="h-auto flex-1 border-0 bg-transparent px-0 py-0 text-lg font-semibold tracking-tight text-foreground shadow-none placeholder:font-normal placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-mr-1 h-7 w-7 shrink-0 p-0"
                aria-label="More actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onClick={() => {
                  // Defer to next tick so radix doesn't steal focus
                  // back to the trigger when the menu closes.
                  setTimeout(() => {
                    const el = document.getElementById(`mq-label-${row.id}`) as HTMLInputElement | null
                    el?.focus()
                    el?.select()
                  }, 0)
                }}
              >
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPatch({ is_active: !state.is_active })}>
                {state.is_active ? 'Disable' : 'Enable'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {state.is_active ? (
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
              Active
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-[10px] uppercase tracking-wider">
              Disabled
            </Badge>
          )}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="cursor-help gap-1 font-mono text-[10px] tracking-tight text-muted-foreground"
                >
                  /q/{row.slug}
                  <Info className="h-3 w-3" aria-hidden />
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                <p className="font-semibold">What the QR physically contains</p>
                <p className="mt-1 font-mono text-[10px] break-all">{scanUrl || '— no destination set —'}</p>
                <p className="mt-1 text-muted-foreground">
                  Customers never see this. Their phone uses it for ~50ms to count the scan, then forwards to the destination.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Destination URL — the one input that *matters* on this card. */}
      <div className="space-y-3 p-4">
        <div className="space-y-1.5">
          <Label htmlFor={`mq-url-${row.id}`} className="text-xs font-medium">
            Where customers go (any public URL works)
          </Label>
          <div className="flex gap-2">
            <Input
              id={`mq-url-${row.id}`}
              value={state.target_url}
              onChange={(e) => onPatch({ target_url: e.target.value })}
              onBlur={() => {
                const fixed = normalizeTargetUrl(state.target_url)
                if (fixed !== state.target_url) onPatch({ target_url: fixed })
              }}
              placeholder="https://…"
              autoComplete="url"
              className="h-9 flex-1 font-mono text-sm"
            />
            <Button
              type="button"
              size="sm"
              disabled={!dirty || saving}
              onClick={onSave}
              className="h-9 shrink-0 gap-1.5"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>
          </div>
          {/* One-tap destinations for the pages this POS already serves.
              Typing "<origin>/q/review" by hand is the kind of thing an owner
              gets subtly wrong once and then prints 200 copies of. */}
          {redirectBase && (
            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              <span className="text-[11px] text-muted-foreground">Quick set:</span>
              {[
                { label: 'Guest feedback', path: '/q/review' },
                { label: 'Loyalty sign-up', path: '/q/join' },
              ].map((preset) => {
                const url = `${redirectBase.replace(/\/+$/, '')}${preset.path}`
                return (
                  <Button
                    key={preset.path}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    disabled={state.target_url === url}
                    onClick={() => onPatch({ target_url: url })}
                  >
                    {preset.label}
                  </Button>
                )
              })}
            </div>
          )}
          {state.target_url.trim() && !isProbablyValidUrl(state.target_url) && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              This doesn't look like a valid link — double-check it before you print this QR.
            </p>
          )}
        </div>
      </div>

      {/* Stats row — small QR thumbnail on the left, 3 always-visible
          30-day stat tiles on the right, Download PNG below. */}
      <div className="flex gap-4 border-t border-border/60 px-4 py-4">
        <div
          className="flex shrink-0 items-center justify-center rounded-md border border-border/60 bg-card p-1.5 ring-1 ring-black/[0.04] dark:bg-card dark:ring-white/10"
          style={{ width: 96, height: 96 }}
        >
          {scanUrl ? (
            <MarketingQrPreview scanUrl={scanUrl} style={state.style} logoDataUrl={logoDataUrl} size={84} />
          ) : (
            <QrCode className="h-8 w-8 text-muted-foreground/40" aria-hidden />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Scans 30d</div>
              <div className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
                {row.scan_count_last_30d.toLocaleString()}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Visitors</div>
              <div className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
                {row.unique_visitors_last_30d.toLocaleString()}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Last scan</div>
              <div className="mt-0.5 truncate text-sm font-medium text-foreground" title={formatRelative(row.last_scanned_at)}>
                {formatRelative(row.last_scanned_at)}
              </div>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-fit gap-1.5"
            onClick={downloadPng}
            disabled={!scanUrl}
          >
            <Download className="h-3.5 w-3.5" />
            Download PNG
          </Button>
        </div>
      </div>

      {/* Disclosures — Customize design (rarely touched) and Scan
          history (occasional). Both collapsed by default. */}
      <Disclosure title="Customize design" icon={Sparkles} hint="Colors, shape, presets">
        <div className="grid gap-5 lg:grid-cols-[176px_1fr]">
          <div className="flex justify-center lg:justify-start">
            <MarketingQrPreview scanUrl={scanUrl} style={state.style} logoDataUrl={logoDataUrl} />
          </div>
          <div className="space-y-4">
            <div className="rounded-md border border-dashed border-muted-foreground/25 bg-muted/30 px-3 py-3 space-y-3">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
                  Color presets
                </p>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((p) => (
                    <PresetChip
                      key={p.name}
                      onClick={() => onPatch({ style: { dark_color: p.dark, light_color: p.light } })}
                    >
                      <span
                        className="mr-2 inline-block h-2.5 w-2.5 rounded-full border border-border/80 shadow-sm"
                        style={{ background: `linear-gradient(135deg, ${p.dark} 50%, ${p.light} 50%)` }}
                        aria-hidden
                      />
                      {p.name}
                    </PresetChip>
                  ))}
                </div>
              </div>
              <div className="h-px bg-border/60" aria-hidden />
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
                  Look presets
                </p>
                <div className="flex flex-wrap gap-2">
                  {LOOK_PRESETS.map((p) => (
                    <PresetChip key={p.name} onClick={() => onPatch({ style: p.patch })}>
                      {p.name}
                    </PresetChip>
                  ))}
                </div>
              </div>
            </div>

            {/* Centre logo. The scale slider is expressed to the admin as
                Small/Medium/Large rather than a raw number, because the
                underlying value is a fraction of the error-correction budget
                and "0.45" means nothing to a restaurant owner. */}
            <div className="rounded-md border border-dashed border-muted-foreground/25 bg-muted/30 px-3 py-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5">
                  <Label htmlFor={`mq-logo-${row.id}`} className="flex items-center gap-1.5 text-xs font-medium">
                    <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    Logo in the centre
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    {logoUrl
                      ? 'Uses your brand logo. Error correction switches to the highest level automatically.'
                      : 'Add a logo under Settings → General to switch this on.'}
                  </p>
                </div>
                <Switch
                  id={`mq-logo-${row.id}`}
                  checked={logoOn(state.style) && Boolean(logoUrl)}
                  disabled={!logoUrl}
                  onCheckedChange={(v) => onPatch({ style: { logo_enabled: v } })}
                />
              </div>
              {logoOn(state.style) && logoUrl ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`mq-logoscale-${row.id}`} className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Logo size
                    </Label>
                    <span className="text-[11px] text-muted-foreground">
                      {logoSizeLabel(state.style.logo_scale)}
                    </span>
                  </div>
                  <input
                    id={`mq-logoscale-${row.id}`}
                    type="range"
                    min={QR_LOGO_SCALE_MIN}
                    max={QR_LOGO_SCALE_MAX}
                    step={0.05}
                    value={state.style.logo_scale ?? QR_LOGO_SCALE_DEFAULT}
                    onChange={(e) => onPatch({ style: { logo_scale: Number(e.target.value) } })}
                    className="w-full accent-primary"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Bigger logos eat into the redundancy that lets a scuffed or dimly-lit code still
                    scan. Always test-scan a print before ordering a batch.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`mq-mod-${row.id}`} className="text-xs font-medium">
                  Module color
                </Label>
                <div className="flex gap-2">
                  <Input
                    id={`mq-mod-${row.id}`}
                    type="color"
                    className="h-9 w-12 shrink-0 cursor-pointer rounded-md border p-1"
                    value={state.style.dark_color}
                    onChange={(e) => onPatch({ style: { dark_color: e.target.value } })}
                  />
                  <Input
                    className="h-9 font-mono text-sm"
                    value={state.style.dark_color}
                    onChange={(e) => onPatch({ style: { dark_color: e.target.value } })}
                    maxLength={7}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`mq-bg-${row.id}`} className="text-xs font-medium">
                  Background color
                </Label>
                <div className="flex gap-2">
                  <Input
                    id={`mq-bg-${row.id}`}
                    type="color"
                    className="h-9 w-12 shrink-0 cursor-pointer rounded-md border p-1"
                    value={state.style.light_color}
                    onChange={(e) => onPatch({ style: { light_color: e.target.value } })}
                  />
                  <Input
                    className="h-9 font-mono text-sm"
                    value={state.style.light_color}
                    onChange={(e) => onPatch({ style: { light_color: e.target.value } })}
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Module shape</Label>
                <Select
                  value={state.style.dots_type}
                  onValueChange={(v: Style['dots_type']) => onPatch({ style: { dots_type: v } })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {marketingQrDotsTypes.map((v) => (
                      <SelectItem key={v} value={v}>
                        {MODULE_SHAPE_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Finder (outer)</Label>
                <Select
                  value={state.style.corner_square_type}
                  onValueChange={(v: Style['corner_square_type']) =>
                    onPatch({ style: { corner_square_type: v } })
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {marketingQrCornerSquareTypes.map((v) => (
                      <SelectItem key={v} value={v}>
                        {FINDER_OUTER_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Finder (inner)</Label>
                <Select
                  value={state.style.corner_dot_type}
                  onValueChange={(v: Style['corner_dot_type']) =>
                    onPatch({ style: { corner_dot_type: v } })
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {marketingQrCornerDotTypes.map((v) => (
                      <SelectItem key={v} value={v}>
                        {FINDER_INNER_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </Disclosure>

      <Disclosure title="Scan history & devices" icon={BarChart3} hint="Chart, range, devices">
        <div className="space-y-4">
          {/* Range tabs. Default 30 days; user can switch independently per card. */}
          <div className="flex flex-wrap gap-1 rounded-md bg-muted/40 p-1">
            {ANALYTICS_RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                onClick={() => setAnalyticsDays(r.days)}
                className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  r.days === analyticsDays
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="h-32 w-full">
            {timeseriesQuery.isLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : allBucketsZero ? (
              <div className="flex h-full items-center justify-center rounded-md border border-dashed border-muted-foreground/30 text-sm text-muted-foreground">
                No scans in the last {selectedRange.label.toLowerCase()} yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={buckets} margin={{ top: 12, right: 5, bottom: 0, left: -25 }}>
                  <defs>
                    <linearGradient id={`mq-grad-${row.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    interval="preserveStartEnd"
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                  />
                  <RechartsTooltip
                    cursor={{ stroke: 'hsl(var(--border))' }}
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="scans"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill={`url(#mq-grad-${row.id})`}
                    name="Scans"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {totals ? (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <p>
                <span className="font-semibold text-foreground">{totals.scans.toLocaleString()}</span>{' '}
                {totals.scans === 1 ? 'scan' : 'scans'} from{' '}
                <span className="font-semibold text-foreground">{totals.unique_visitors.toLocaleString()}</span>{' '}
                {totals.unique_visitors === 1 ? 'visitor' : 'visitors'} in the last{' '}
                {selectedRange.label.toLowerCase()}.
              </p>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="shrink-0 text-muted-foreground/70 transition-colors hover:text-foreground"
                      aria-label="How scans and visitors are counted"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-sm leading-relaxed">
                    A visitor is one device — the same phone scanning several times counts once.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ) : null}

          {totals ? <DeviceMixBar mix={totals.device_mix} /> : null}
        </div>
      </Disclosure>
    </Card>
  )
}

interface AdminMarketingQrSettingsProps {
  /**
   * When true, the outer page-level header card is suppressed so this
   * component can be slotted into another shell (e.g. MarketingShell's
   * CardContent in /admin/marketing/qr-codes) without duplicating the
   * "Marketing QR codes" headline.
   */
  embedded?: boolean
}

// Hard ceiling mirroring backend's maxQRCodesPerTenant. If they ever
// diverge, the backend's 409 is the source of truth; this is only for
// the disable-the-button + count chip on the frontend.
const MAX_QR_CODES = 50

// Templates shown on "+ New QR code". Picking one seeds the label so
// the card lands as "Google Reviews" instead of "QR 7". URLs are still
// blank — the owner pastes the destination after creation. "Custom"
// falls back to the backend's auto-numbered name.
type QrTemplate = { label: string; description: string; icon: LucideIcon }
const QR_TEMPLATES: QrTemplate[] = [
  { label: 'Google Reviews', description: 'Send happy customers to your Google listing.', icon: Star },
  { label: 'Menu', description: 'Link to your digital menu or PDF.', icon: BookOpen },
  { label: 'Instagram', description: 'Drive followers to your profile.', icon: Instagram },
  { label: 'Website', description: 'Your main site or landing page.', icon: Globe },
  { label: 'Loyalty program', description: 'Sign-up link for your rewards program.', icon: Gift },
  { label: 'Custom', description: 'Blank QR — pick a name and URL yourself.', icon: Sparkles },
]

export function AdminMarketingQrSettings({ embedded = false }: AdminMarketingQrSettingsProps = {}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const listQuery = useQuery<APIResponse<MarketingQRListResponse>>({
    queryKey: ['marketing-qr', 'list'],
    queryFn: () => apiClient.listMarketingQR(),
  })

  // Per-row draft state, keyed by row id. Resets whenever the server
  // returns a new list so on-disk wins and the dirty indicator only
  // reflects actual local edits.
  const [drafts, setDrafts] = useState<Record<string, SlotState>>({})
  const serverItems = listQuery.data?.data?.items ?? []
  const redirectBase = listQuery.data?.data?.redirect_base ?? ''
  // `source` on the list response is intentionally not surfaced to the
  // restaurant admin. The tracking server is managed by Bhookly via env
  // var / auto-detect from the request host (nginx preserves it). If you
  // need to inspect or override it, build a super-admin tool that calls
  // PUT /admin/marketing/qr/config — that route is still wired up.

  useEffect(() => {
    if (!serverItems.length) return
    setDrafts((prev) => {
      const next: Record<string, SlotState> = {}
      for (const row of serverItems) {
        next[row.id] = prev[row.id] ?? rowToState(row)
      }
      return next
    })
  }, [serverItems])

  const saveMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateMarketingQRRequest }) => {
      await apiClient.updateMarketingQR(id, data)
    },
    onSuccess: () => {
      toast({ title: 'Saved', description: 'Marketing QR updated.' })
      queryClient.invalidateQueries({ queryKey: ['marketing-qr', 'list'] })
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e)
      toast({ title: 'Save failed', description: msg, variant: 'destructive' })
    },
  })

  // Track the id of the most-recently-created QR so we can briefly
  // highlight its card with a ring after the list refetches. Clears
  // itself after ~3s so the highlight doesn't linger forever.
  const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null)
  useEffect(() => {
    if (!newlyCreatedId) return
    const t = setTimeout(() => setNewlyCreatedId(null), 3000)
    return () => clearTimeout(t)
  }, [newlyCreatedId])

  const createMutation = useMutation({
    mutationFn: (label?: string) => apiClient.createMarketingQR(label),
    onSuccess: (res) => {
      toast({
        title: 'QR code created',
        description: 'Set its destination URL to start tracking scans.',
      })
      const newId = res.data?.id
      if (newId) setNewlyCreatedId(newId)
      queryClient.invalidateQueries({ queryKey: ['marketing-qr', 'list'] })
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e)
      toast({ title: 'Could not create QR', description: msg, variant: 'destructive' })
    },
  })

  // Track which row's Delete dialog is open. Null = no dialog. The
  // row's scan_count_total is read off the cached list response so
  // we can show "this will delete N recorded scans" without a fetch.
  const [pendingDelete, setPendingDelete] = useState<MarketingQRCodeRow | null>(null)

  const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
  const handlePickTemplate = (tpl: QrTemplate) => {
    setTemplatePickerOpen(false)
    createMutation.mutate(tpl.label === 'Custom' ? undefined : tpl.label)
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteMarketingQR(id),
    onSuccess: () => {
      toast({ title: 'QR code deleted', description: 'Already-printed copies will now 404 when scanned.' })
      setPendingDelete(null)
      queryClient.invalidateQueries({ queryKey: ['marketing-qr', 'list'] })
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e)
      toast({ title: 'Delete failed', description: msg, variant: 'destructive' })
    },
  })

  const patchDraft = useCallback(
    (id: string, patch: Partial<SlotState> | { style: StylePatch }) => {
      setDrafts((prev) => {
        const cur = prev[id]
        if (!cur) return prev
        const styleField = 'style' in patch ? patch.style : undefined
        const rest = 'style' in patch ? {} : (patch as Partial<SlotState>)
        const nextStyle = styleField ? clampFixedStyle({ ...cur.style, ...styleField }) : cur.style
        return {
          ...prev,
          [id]: { ...cur, ...rest, style: nextStyle },
        }
      })
    },
    [],
  )

  const saveSlot = (row: MarketingQRCodeRow) => {
    const draft = drafts[row.id]
    if (!draft) return
    const original = rowToState(row)
    const diff = statePatchToRequest(original, draft)
    if (Object.keys(diff).length === 0) return
    saveMutation.mutate({ id: row.id, data: diff })
  }

  const isDirty = (row: MarketingQRCodeRow): boolean => {
    const draft = drafts[row.id]
    if (!draft) return false
    return Object.keys(statePatchToRequest(rowToState(row), draft)).length > 0
  }

  const atLimit = serverItems.length >= MAX_QR_CODES
  const loading = listQuery.isLoading

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-6 max-w-6xl'}>
      {!embedded && (
        <header className="min-w-0 space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">Marketing QR codes</h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
            Trackable codes for menus, promos, and delivery links. Edit the destination any time — the printed
            code never changes. Scans count in real time; known link-preview crawlers are filtered out.
          </p>
        </header>
      )}

      {loading ? (
        <div className="flex justify-center rounded-xl border border-dashed py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-label="Loading marketing QR codes" />
        </div>
      ) : serverItems.length === 0 ? (
        // Empty state — fresh tenant, no QRs yet. Single big CTA.
        <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-muted-foreground/30 bg-muted/20 px-6 py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
            <QrCode className="h-7 w-7" aria-hidden />
          </div>
          <div className="space-y-1.5 max-w-md">
            <h3 className="text-lg font-semibold text-foreground">No QR codes yet</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Create your first trackable QR. Print it on a menu, table tent, poster, or anywhere customers might
              scan. We'll count every scan and tell you which campaigns are pulling weight.
            </p>
          </div>
          <Button
            type="button"
            size="default"
            className="gap-1.5"
            onClick={() => setTemplatePickerOpen(true)}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Create your first QR code
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">
              {serverItems.length} of {MAX_QR_CODES} used
            </span>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={atLimit ? 'cursor-not-allowed' : ''}>
                    <Button
                      type="button"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => setTemplatePickerOpen(true)}
                      disabled={atLimit || createMutation.isPending}
                    >
                      {createMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Plus className="h-3.5 w-3.5" />
                      )}
                      New QR code
                    </Button>
                  </span>
                </TooltipTrigger>
                {atLimit ? (
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    You've hit the {MAX_QR_CODES}-QR limit. Delete an unused code first.
                  </TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {[...serverItems].reverse().map((row) => {
              const draft = drafts[row.id] ?? rowToState(row)
              return (
                <MarketingQRCard
                  key={row.id}
                  row={row}
                  redirectBase={redirectBase}
                  state={draft}
                  onPatch={(patch) => patchDraft(row.id, patch)}
                  onSave={() => saveSlot(row)}
                  saving={
                    saveMutation.isPending &&
                    saveMutation.variables !== undefined &&
                    (saveMutation.variables as { id: string }).id === row.id
                  }
                  dirty={isDirty(row)}
                  isNew={newlyCreatedId === row.id}
                  onDelete={() => setPendingDelete(row)}
                />
              )
            })}
          </div>
        </>
      )}

      <Dialog open={templatePickerOpen} onOpenChange={setTemplatePickerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>What's this QR for?</DialogTitle>
            <DialogDescription>
              Pick one — you can rename it and set the URL after.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 py-3 sm:grid-cols-2">
            {QR_TEMPLATES.map((tpl) => {
              const Icon = tpl.icon
              return (
                <button
                  key={tpl.label}
                  type="button"
                  onClick={() => handlePickTemplate(tpl)}
                  disabled={createMutation.isPending}
                  className="flex items-start gap-3 rounded-md border border-border/70 bg-background px-3 py-2.5 text-left transition-colors hover:border-primary/50 hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{tpl.label}</div>
                    <div className="text-xs leading-snug text-muted-foreground">{tpl.description}</div>
                  </div>
                </button>
              )
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Delete &ldquo;{pendingDelete?.label?.trim() || `/q/${pendingDelete?.slug}`}&rdquo;?
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              This permanently deletes the QR code
              {pendingDelete && pendingDelete.scan_count_total > 0 ? (
                <>
                  {' and all '}
                  <span className="font-semibold text-foreground">
                    {pendingDelete.scan_count_total.toLocaleString()} recorded scans
                  </span>
                  {' '}for it
                </>
              ) : null}
              . Already-printed copies of this code will show &ldquo;QR code unavailable&rdquo; when anyone scans
              them &mdash; this can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPendingDelete(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (pendingDelete) deleteMutation.mutate(pendingDelete.id)
              }}
              disabled={deleteMutation.isPending}
              className="gap-1.5"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
